<template>
  <div class="register-page">
    <div class="register-container">
      <div class="register-form">
        <div class="logo">
          <img src="../assets/logo.png" alt="Nvoko Logo" />
        </div>

        <h2>Nice to meet you!</h2>
        <h3>Let's get to know each other.</h3>

        <form @submit.prevent="onSignUpClick">
          <div class="inputs">
            <div
              class="flex flex-row items-center justify-between gap-1.5 w-full pb-4"
            >
              <input
                v-model="firstName"
                id="firstName"
                type="text"
                class="text-input dark small-text"
                placeholder="First name"
              />
              <input
                v-model="lastName"
                id="lastName"
                type="text"
                class="text-sm text-input dark small-text"
                placeholder="Last name"
              />
            </div>
            <div class="form-group">
              <input
                v-model="email"
                id="email"
                type="email"
                class="text-input dark"
                placeholder="Email address"
              />
            </div>
            <div class="form-group">
              <input
                v-model="password"
                id="password"
                type="password"
                class="text-input dark"
                placeholder="Password"
              />
            </div>

            <div class="terms-checkbox">
              <input
                v-model="acceptedTerms"
                class="styled-checkbox"
                id="styled-checkbox-1"
                type="checkbox"
                value="true"
              />
              <label for="styled-checkbox-1">
                <span>
                  I agree with Nvoko's
                  <a
                    :href="termsUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="terms-link"
                  >
                    terms and conditions
                  </a>
                  and I'm over the age of 18.
                </span>
              </label>
            </div>

            <div class="terms-checkbox">
              <input
                v-model="acceptedLegalDisclaimer"
                class="styled-checkbox"
                id="styled-checkbox-legal"
                type="checkbox"
                value="true"
              />
              <label for="styled-checkbox-legal">
                <span>
                  Any information contained on Nvoko is not legal advice and
                  should not be treated as such. You should always contact an
                  attorney for help with your specific legal needs and issues.
                </span>
              </label>
            </div>
          </div>

          <button
            type="submit"
            class="primary"
            :disabled="!enableSignupButton || isLoading"
          >
            {{ signupButtonText }}
          </button>
        </form>

        <div class="login-link">
          Already have an account?
          <router-link to="/" class="accent-link">Login</router-link>
          instead.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { normalizeEmail } from "@/utils/normalizeEmail";
import REGISTER from "@/api/mutations/REGISTER.gql";

export default {
  name: "RegisterPage",
  props: {
    initialEmail: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      firstName: "",
      lastName: "",
      email: this.initialEmail || "",
      password: "",
      acceptedTerms: false,
      acceptedLegalDisclaimer: false,
      isLoading: false,
      termsUrl: "/terms-of-use"
    };
  },
  computed: {
    enableSignupButton() {
      return (
        this.firstName &&
        this.lastName &&
        this.email &&
        this.password &&
        this.acceptedTerms &&
        this.acceptedLegalDisclaimer
      );
    },
    signupButtonText() {
      return this.isLoading ? "Loading..." : "Create Account";
    }
  },
  methods: {
    async onSignUpClick() {
      try {
        if (!this.enableSignupButton) return;

        this.isLoading = true;

        const {
          data: { register }
        } = await this.$apollo.mutate({
          mutation: REGISTER,
          variables: {
            email: normalizeEmail(this.email),
            password: this.password.trim(),
            first_name: this.firstName.trim(),
            last_name: this.lastName.trim(),
            legal_disclaimer_accepted: this.acceptedLegalDisclaimer
          }
        });

        this.$token = register.token;
        this.$store.commit("account/authSuccess", register);

        if (register.user.status === "unverified") {
          this.$router.push({ name: "SignupVerification" });
        } else if (!register.user.onboarding_complete) {
          this.$router.push({ name: "Onboarding" });
        } else {
          this.$router.push({ name: "Catalog" });
        }
      } catch (e) {
        this.$errorService.handleApolloError(e, "registration");
        this.isLoading = false;
      }
    }
  }
};
</script>

<style scoped lang="scss">
@import "../styles/_mixins.scss";

.register-page {
  background: linear-gradient(
    to bottom right,
    rgba(0, 0, 0, 0.95),
    rgba(20, 20, 20, 0.9)
  );
  min-height: 100vh;
  width: 100%;
  position: relative;
  color: white;
  overflow-x: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("/path-to-your-texture.png") repeat;
    opacity: 0.03;
    pointer-events: none;
  }

  .nav {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 15%;
    padding-top: 30px;
    position: relative;
    z-index: 1;

    .logo {
      padding: 7px;
      justify-content: flex-start;
      align-items: flex-start;
      display: flex;
    }
  }

  .register-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: calc(100vh - 120px);
    position: relative;
    z-index: 1;

    .register-form {
      background: linear-gradient(
        145deg,
        rgba(40, 40, 40, 0.6),
        rgba(20, 20, 20, 0.8)
      );
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      border-radius: 24px;
      padding: 60px;
      width: 550px;
      max-width: 90%;
      box-shadow: 0 4px 30px rgba(0, 0, 0, 0.3),
        0 1px 1px rgba(255, 255, 255, 0.05) inset;
      border: 1px solid rgba(255, 255, 255, 0.05);
      display: flex;
      flex-direction: column;
      align-items: center;

      .logo {
        margin-bottom: 32px;
        img {
          max-width: 150px;
          filter: drop-shadow(0 0 10px rgba(255, 255, 255, 0.1));
        }
      }

      h2 {
        color: $white;
        font-size: 28px;
        margin-bottom: 10px;
        font-weight: 600;
        letter-spacing: -0.02em;
        background: linear-gradient(to right, #ffffff, #e0e0e0);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
      }

      h3 {
        color: rgba(255, 255, 255, 0.9);
        font-size: 18px;
        font-weight: 400;
        margin-bottom: 20px;
      }

      form {
        margin-top: 20px;
        margin-bottom: 8px;
        width: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;

        .inputs {
          width: 100%;

          .flex {
            display: flex;

            input {
              width: calc(50% - 5px);
              background: rgba(0, 0, 0, 0.2);
              border: 1px solid rgba(255, 255, 255, 0.1);
              transition: all 0.3s ease;

              &:focus {
                border-color: rgba(255, 255, 255, 0.2);
                box-shadow: 0 0 15px rgba(255, 255, 255, 0.05);
              }
            }
          }

          .form-group {
            margin-bottom: 24px;

            input {
              @include details-light();
              color: $white;
              width: 100%;
              background: rgba(0, 0, 0, 0.2);
              border: 1px solid rgba(255, 255, 255, 0.1);
              transition: all 0.3s ease;

              &:focus {
                border-color: rgba(255, 255, 255, 0.2);
                box-shadow: 0 0 15px rgba(255, 255, 255, 0.05);
              }
            }
          }

          .terms-checkbox {
            display: flex;
            align-items: flex-start;
            margin-bottom: 24px;
            color: rgba(255, 255, 255, 0.8);

            input {
              margin-top: 5px;
              margin-right: 10px;
            }

            .terms-link {
              color: $accent;
              text-decoration: none;
              transition: all 0.2s ease;

              &:hover {
                color: lighten($accent, 10%);
                text-shadow: 0 0 10px rgba($accent, 0.3);
              }
            }
          }
        }

        button {
          font-weight: 500;
          font-size: 16px;
          line-height: 24px;
          letter-spacing: -0.22px;
          padding: 12px 40px;
          margin-top: 10px;
          background: linear-gradient(135deg, $accent, darken($accent, 15%));
          border-radius: 30px;
          transition: all 0.3s ease;

          &:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
          }

          &:disabled {
            opacity: 0.7;
            cursor: not-allowed;
          }
        }
      }

      .login-link {
        margin-top: 30px;
        text-align: center;
        color: rgba(255, 255, 255, 0.7);

        .accent-link {
          color: $accent;
          font-weight: 600;
          margin-left: 5px;
          transition: all 0.2s ease;

          &:hover {
            color: lighten($accent, 10%);
            text-decoration: none;
            text-shadow: 0 0 10px rgba($accent, 0.3);
          }
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .register-page {
    .nav {
      padding: 0 5%;

      .logo {
        width: 30%;
      }
    }

    .register-container {
      .register-form {
        padding: 30px 20px;

        form {
          .inputs {
            .flex {
              flex-direction: column;

              input {
                width: 100%;
                margin-bottom: 24px;
              }
            }
          }
        }
      }
    }
  }
}
.register-page .register-container {
  min-height: 100vh;
  padding: 24px 16px;
}
.register-page .register-container .register-form {
  width: 495px;
  padding: 32px;
  max-width: 100%;
  border-radius: 16px;
}
.register-page .register-container .register-form .logo {
  margin-bottom: 20px;
}
.register-page .register-container .register-form form {
  margin-top: 12px;
}
.register-page .register-container .register-form .form-group {
  margin-bottom: 16px;
}
.register-page input::placeholder {
  color: #c8bdc2;
  opacity: 1;
}
@media (max-width: 480px) {
  .register-page .register-container .register-form {
    padding: 24px;
  }
}
</style>
